import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ChevronRight, Calendar, TrendingUp } from 'lucide-react';
import { Container, Button } from '../components/ui';

/*
 * Investors
 * ---------
 * Landing page for /investors, reached from the sidebar drawer's
 * "Our Investors" mega-panel. Two zones:
 *
 *   1. Cover-video hero identical in treatment to the /services and
 *      /services/<slug> pages so the site's "chapter" pages all share
 *      the same visual language.
 *
 *   2. "Meet our investors" section — an editorial split-tile per
 *      investor: portrait photo on the left, name + role + a pair of
 *      funding stats + backing narrative on the right. Everything is
 *      visible at once (no flip animation / hover reveal) so the page
 *      reads well even for a single investor and gracefully scales
 *      when more join. Cards alternate photo side (left / right / …)
 *      as investors are added, giving vertical rhythm.
 *
 * Data lives inline here (`INVESTORS`) since the list is short and
 * the fields are unique to this page. Move it into `src/data/` if the
 * list grows past a handful.
 */

// ---------------------------------------------------------------------------
// Media + data
// ---------------------------------------------------------------------------

const HERO_MEDIA =
  'https://istatec-assets.istatec.com/istatec_our_investors_cover.webm';

interface Investor {
  id: string;
  name: string;
  role: string;
  photo: string;
  /**
   * Headline funding amount for the "Raised" stat, e.g. "₹6.5M".
   * Kept as a display string (not a number) so the currency symbol
   * and unit stay under content control.
   */
  fundingAmount: string;
  /**
   * When the round closed, shown next to the amount, e.g.
   * "July 2026".
   */
  fundingDate: string;
  /**
   * Short human-voiced sentence explaining what the backing has
   * unlocked for the company. Renders as a paragraph below the
   * stats.
   */
  contribution: string;
}

const INVESTORS: Investor[] = [
  {
    id: 'rk-choudhary',
    name: 'Rakesh Kumar Chaudhary',
    role: 'Ex. Officer, WOAFS',
    photo: 'https://istatec-assets.istatec.com/investor_RK_Choudhary.png',
    fundingAmount: '₹6.5M',
    fundingDate: 'July 2026',
    contribution:
      "Rakesh's backing is directly funding our R&D, expanded engineering hires, and the platform work our teams ship for clients.",
  },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const HEADER_OFFSET_PX = 80; // matches Header's h-20

export const Investors: React.FC = () => {
  return (
    <>
      {/* ═════════════════ Breadcrumb bar ═════════════════
          Sticky under the fixed header — mirrors the pattern used on
          the /services + /services/<slug> pages so the "Home >
          Investors" trail stays anchored while the user scrolls
          through the hero and tile grid. */}
      <div style={{ height: `${HEADER_OFFSET_PX}px` }} aria-hidden />
      <div className="sticky top-20 z-40 bg-white border-b border-navy-100 shadow-sm">
        <Container className="py-4">
          <Breadcrumbs />
        </Container>
      </div>

      {/* ═════════════════ Cover hero ═════════════════ */}
      <InvestorsHero />

      {/* ═════════════════ Investor tiles ═════════════════ */}
      <InvestorTilesSection />
    </>
  );
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const Breadcrumbs: React.FC = () => (
  <nav
    aria-label="Breadcrumb"
    className="flex flex-wrap items-center gap-2 text-sm"
  >
    <Link
      to="/"
      className="text-navy-500 hover:text-accent-600 transition-colors"
    >
      Home
    </Link>
    <ChevronRight className="w-3.5 h-3.5 text-navy-400 shrink-0" aria-hidden />
    <span aria-current="page" className="font-semibold text-navy-900">
      Investors
    </span>
  </nav>
);

/**
 * Cover video hero + overlay card. Uses the same responsive height
 * math (`calc(56.25vw - 200px)`, min-h 340) as the service pages so
 * the site's landing-style pages read as one family.
 */
const InvestorsHero: React.FC = () => {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    const p = v.play();
    if (p && typeof p.catch === 'function') p.catch(() => {});
  }, []);

  return (
    <section className="relative bg-navy-950">
      <div
        className="responsive-hero relative w-full overflow-hidden"
      >
        <video
          ref={videoRef}
          src={HERO_MEDIA}
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div aria-hidden className="absolute inset-0 bg-black/35" />

        <div className="relative flex items-center justify-center px-4 sm:px-6 py-10 sm:py-14 lg:py-20">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className={
              'w-full max-w-3xl md:max-w-4xl lg:max-w-5xl xl:max-w-6xl ' +
              'bg-white rounded-2xl border border-navy-100 ' +
              'shadow-[0_25px_70px_-15px_rgba(2,8,20,0.6)] ' +
              'p-5 sm:p-8 md:p-10 lg:p-12'
            }
          >
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-[54px] font-bold text-navy-900 leading-tight mb-4 md:mb-6">
              Our Investors
            </h1>
            <p className="text-base sm:text-lg md:text-xl lg:text-[22px] text-navy-700 leading-relaxed mb-7 md:mb-9">
              We&rsquo;re proud to have investors who believe in what
              we&rsquo;re building — partners whose backing helps us grow the
              team, deepen the R&amp;D, and stay honest about what we ship.
            </p>
            <Link to="/contact">
              <Button
                variant="primary"
                size="lg"
                rightIcon={<ArrowRight className="w-5 h-5" />}
              >
                Get in touch
              </Button>
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

/**
 * "Meet our investors" section. Renders one vertical-stack profile
 * card per entry in `INVESTORS`. The intro heading + subline stay
 * left-anchored so they match the language on `/services` and
 * `/istalabs`, and the card grid below them lays out cards ~1/3 of
 * the viewport wide, left-aligned.
 */
const InvestorTilesSection: React.FC = () => (
  <section className="bg-gradient-to-b from-white via-navy-50/60 to-white py-16 md:py-24 lg:py-28">
    <div className="site-wide-container">
      <div className="max-w-4xl mb-12 md:mb-14">
        <motion.p
          className="text-sm md:text-base font-semibold uppercase tracking-[0.12em] text-accent-600 mb-4"
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        >
          Backed by belief
        </motion.p>
        <motion.h2
          className="text-3xl sm:text-4xl md:text-5xl lg:text-[52px] font-bold text-navy-950 leading-[1.16] mb-5 md:mb-6"
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.4, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
        >
          Meet our investors
        </motion.h2>
        <motion.p
          className="text-base md:text-lg lg:text-xl text-navy-600 leading-relaxed"
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.4, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
        >
          The people whose belief in this company translates into real
          capacity — new hires, new capabilities, and a longer runway to keep
          building thoughtfully.
        </motion.p>
      </div>

      {/*
        Grid: 3 columns on md+ so each card naturally occupies 1/3 of
        the section width (which is ~1/3 of the viewport once the
        section's horizontal padding is accounted for). `grid` (not
        `flex`) is deliberate — it keeps the column tracks the same
        width even if some rows have fewer cards than others, so
        future additions line up cleanly. `items-start` prevents the
        cards from stretching to match the tallest one in the row.
      */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 md:gap-10 items-start">
        {INVESTORS.map((inv) => (
          <InvestorProfileCard key={inv.id} investor={inv} />
        ))}
      </div>
    </div>
  </section>
);

/**
 * Vertical-stack profile card. Photo sits on TOP of the card and the
 * profile block (role → name → funding stats → narrative) reads
 * naturally underneath it — same visual grammar as a magazine
 * "portrait + caption" tile.
 *
 * The card itself is intentionally not full-width; the parent grid
 * places it into a 1/3-wide column so the tile reads as a poster,
 * not a banner.
 */
const InvestorProfileCard: React.FC<{
  investor: Investor;
}> = ({ investor }) => {
  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={
        'group relative overflow-hidden ' +
        'rounded-3xl bg-white ring-1 ring-navy-100 ' +
        'shadow-[0_25px_70px_-25px_rgba(2,8,20,0.35)] ' +
        'hover:shadow-[0_35px_90px_-25px_rgba(2,8,20,0.45)] ' +
        'transition-shadow duration-500'
      }
    >
      {/* ────────── Photo (top) ──────────
          The source photo is 1024×1024 (square). We reserve a
          matching `aspect-square` frame so the browser doesn't need
          to crop anything — the entire source renders 1:1 inside the
          card, face-to-hands, no matter how wide the 1/3-column
          becomes at different breakpoints. That's more reliable than
          a 4:5 portrait crop with `object-top`, which was clipping
          the face in narrower columns because the subject sits
          slightly off-center in the source. `object-cover` stays as
          a safety net if a future investor's photo ships with a
          different aspect ratio. */}
      <div className="relative bg-navy-100 overflow-hidden aspect-square">
        <img
          src={investor.photo}
          alt={investor.name}
          className={
            'absolute inset-0 w-full h-full object-contain ' +
            'transition-transform duration-[900ms] ease-out ' +
            'group-hover:scale-[1.04]'
          }
          loading="lazy"
        />
        {/* Soft bottom-right vignette adds depth without darkening
            the subject. */}
        <div
          aria-hidden
          className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-navy-950/25"
        />
      </div>

      {/* ────────── Content (below photo) ────────── */}
      <div className="p-6 sm:p-7 md:p-8">
        {/* Role eyebrow */}
        <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-accent-600 mb-3">
          {investor.role}
        </p>

        {/* Name — the anchor of the whole card. Sized to feel
            confident inside a narrower 1/3-width tile. */}
        <h3 className="text-2xl md:text-[28px] font-bold text-navy-950 leading-[1.15] mb-6 md:mb-7">
          {investor.name}
        </h3>

        {/* Funding stats — two columns split by a divider. Kept
            compact so the stats read at a glance in a smaller tile
            without dominating the composition. */}
        <div className="grid grid-cols-2 gap-4 py-5 border-y border-navy-100 mb-6">
          <div>
            <div className="flex items-center gap-1.5 mb-1.5">
              <TrendingUp className="w-3.5 h-3.5 text-accent-600" aria-hidden />
              <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-navy-500">
                Total raised
              </p>
            </div>
            <p className="text-xl md:text-2xl font-bold text-navy-950 leading-none">
              {investor.fundingAmount}
            </p>
          </div>
          <div>
            <div className="flex items-center gap-1.5 mb-1.5">
              <Calendar className="w-3.5 h-3.5 text-accent-600" aria-hidden />
              <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-navy-500">
                Round closed
              </p>
            </div>
            <p className="text-xl md:text-2xl font-bold text-navy-950 leading-none">
              {investor.fundingDate}
            </p>
          </div>
        </div>

        {/* Narrative — soft, human-voiced sentence about impact */}
        <p className="text-sm md:text-[15px] text-navy-700 leading-relaxed">
          {investor.contribution}
        </p>
      </div>

      {/* Subtle accent bar along the top edge — reinforces the
          "featured" feel of the card without an icon or badge. */}
      <div
        aria-hidden
        className={
          'pointer-events-none absolute inset-x-0 top-0 h-1 ' +
          'bg-gradient-to-r from-accent-400 via-accent-500 to-accent-300'
        }
      />
    </motion.article>
  );
};
